import React, { useEffect, useState } from "react";

import { Copy } from "icons";
import { Download } from "icons";

import { generatePdf } from "apis/dataConsumptionExports";
import {
  Button,
  Toastr,
  Pagination,
  PageLoader,
  Table,
} from "clearsense-ui/components";
import copyToClipboard from "utils/copyToClipboard";

import { COLUMN_DATA, EXPORT_CSV, EXPORT_PDF, SEARCH_CODE } from "../constants";

const SearchResultTable = ({
  isLoading,
  data,
  rowData,
  setRowData,
  searchType,
  type,
  searchValue,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const totalCount = rowData.length;
  const [isExportPdfFileLoading, setIsExportPdfFileLoading] = useState(false);
  const [isExportCsvFileLoading, setIsExportCsvFileLoading] = useState(false);

  const formatRowData = () => {
    if (Array.isArray(data)) {
      setRowData(
        data?.map(({ Predicted_Code, Predicted_Description }) => ({
          code: Predicted_Code,
          description: Predicted_Description,
        }))
      );
    } else if (data) {
      const code = data?.code;
      if (Array.isArray(data?.description)) {
        setRowData(
          data?.description?.map(desc => ({ code: code, description: desc }))
        );
      } else if (data?.description) {
        setRowData([{ code: code, description: data?.description }]);
      }
    }
  };

  useEffect(() => {
    formatRowData();
  }, [data]);

  useEffect(() => {
    if (pageSize * currentPage > rowData.length) {
      setCurrentPage(1);
    }
  }, [pageSize]);

  const downloadFile = (fileFormat, data) => {
    const hiddenElement = document.createElement("a");
    hiddenElement.href =
      fileFormat === EXPORT_CSV
        ? `data:text/csv;charset=utf-8,${encodeURI(data)}`
        : window.URL.createObjectURL(new Blob([data]));
    hiddenElement.target = "_blank";
    hiddenElement.download = `${type}.${fileFormat}`;
    hiddenElement.click();
  };

  const handlePdfExport = async () => {
    setIsExportPdfFileLoading(true);
    try {
      const pdfData = await generatePdf({
        data: rowData,
        value: searchValue,
        type: type,
        is_search_code: searchType.value === SEARCH_CODE.value,
      });
      downloadFile(EXPORT_PDF, pdfData.data);
    } catch (error) {
      logger.error(error);
      Toastr.error("Error while generating pdf");
    } finally {
      setIsExportPdfFileLoading(false);
    }
  };

  const handleCsvExport = () => {
    setIsExportCsvFileLoading(true);
    let csvData =
      searchType.value === SEARCH_CODE.value
        ? `DESCRIPTION (Search Results for "${rowData[0].code}")\n`
        : "CODE,DESCRIPTION\n";
    rowData.forEach(({ code, description }) => {
      csvData +=
        searchType.value === SEARCH_CODE.value
          ? `"${description}"\n`
          : `${code},"${description}"\n`;
    });
    downloadFile(EXPORT_CSV, csvData);
    setIsExportCsvFileLoading(false);
  };

  const copyAction = {
    title: (
      <div className="flex space-x-2">
        <Button
          className="whitespace-nowrap"
          icon={Download}
          label="Export PDF"
          size="small"
          style="secondary"
          loading={isExportPdfFileLoading}
          disabled={isExportPdfFileLoading}
          onClick={() => {
            handlePdfExport();
          }}
          data-cy="icd-rxnorm-pdf-download-button"
        />
        <Button
          className="whitespace-nowrap"
          label="Export CSV"
          size="small"
          style="secondary"
          icon={Download}
          loading={isExportCsvFileLoading}
          disabled={isExportCsvFileLoading}
          onClick={() => {
            handleCsvExport();
          }}
          data-cy="icd-rxnorm-csv-download-button"
        />
      </div>
    ),
    dataIndex: "id",
    width: 275,
    key: "id",
    render: (_, record) => (
      <div className="flex justify-end">
        <Button
          data-cy="copy-row-data-button"
          style="text"
          icon={() => <Copy size={24} />}
          onClick={() => {
            if (searchType.value === SEARCH_CODE.value) {
              copyToClipboard(record.description, "Copied to clipboard");
            } else {
              copyToClipboard(
                `${record.code}: ${record.description}`,
                "Copied to clipboard"
              );
            }
          }}
        />
      </div>
    ),
  };
  if (!rowData.length && isLoading) {
    return (
      <div className="flex justify-between p-5 mx-8 mt-12">
        <PageLoader />
      </div>
    );
  }

  return (
    !!rowData.length && (
      <div
        className={
          "flex flex-col justify-between flex-grow p-5 mx-8 mt-12 cs-ui-table__hide-checkbox cs-ui-table-background"
        }
        data-cy="icd-rxnorm-result-table-container"
      >
        <Table
          data-cy="result-table"
          fixedHeight
          columnData={
            searchType.value === SEARCH_CODE.value
              ? [COLUMN_DATA[1], copyAction]
              : [...COLUMN_DATA, copyAction]
          }
          rowData={rowData.length && rowData}
          currentPageNumber={currentPage}
          paginationProps={{ pageSize: pageSize }}
          scroll={{ x: "max-content" }}
          isLoading={isLoading}
          className="h-full bg-white"
        />
        <Pagination
          className="px-2 py-5 mt-auto"
          totalRecordsCount={totalCount}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          showResultsPerPage
          setPageSize={setPageSize}
        />
      </div>
    )
  );
};

export default SearchResultTable;
